import { AddToDraftReportDialog } from "@/components/billing/add-to-draft-report-dialog";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingLabel } from "@/components/ui/loading-label";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import type { WorkflowAction as WorkflowMoveAction } from "@/components/work-items/row-actions";
import type { WorkItemRow } from "@/components/work-items/work-items-table";
import { api } from "@/lib/api";
import { displayStateName } from "@/lib/workflow";
import { useNavigate, useRouter } from "@tanstack/react-router";
import {
  AlertCircle,
  FileSpreadsheet,
  FileText,
  Tag,
  Trash2,
  User,
  Users,
  Workflow,
} from "lucide-react";
import { useEffect, useState } from "react";
import { SlotPicker } from "./slot-picker";

/**
 * Floating bulk-edit toolbar for the work-items list.
 *
 * Hits `PATCH /api/work-items/bulk` for priority / assignee / release /
 * team slots / delete. Workflow status is a separate button ("Move") that
 * hits `POST /api/work-items/bulk-actions` — it does NOT write a status
 * directly; it fans the same per-item `executeAction` out over the
 * selection, one action id at a time, so every row still gets its own
 * state/role/blocker checks and GitLab write-through. Only actions
 * available on EVERY selected row are offered (the intersection of each
 * row's server-resolved `availableActions`), so there is nothing here
 * that a row's own action buttons wouldn't already allow.
 *
 * The server answers `{count, skipped[]}` with the honest changed-row
 * count; a partial result surfaces the per-row skip reasons instead of
 * pretending everything applied.
 *
 * A second, stacked bar ("Create report" / "Send to existing report")
 * surfaces for admin/accountant when every ticked row is a fixed-price
 * work item with a price set, shares one customer, and isn't already on
 * a report — mirrors the time-entries "report" bar
 * (`time-entries/list/bulk-bars.tsx`). Hours-mode work items bill
 * through their time entries instead, via that TE-selection flow.
 */

type Option = { id: string; name: string };
type BulkAction = "assignee" | "priority" | "release" | "team" | "delete" | "move";
type TeamSlot =
  | "manager"
  | "developer"
  | "tester"
  | "maintainer"
  | "productionTester"
  | "productionMaintainer";

const PRIORITIES: Option[] = [
  { id: "low", name: "Low" },
  { id: "normal", name: "Normal" },
  { id: "high", name: "High" },
  { id: "urgent", name: "Urgent" },
];

const TEAM_SLOTS: Array<{ id: TeamSlot; name: string }> = [
  { id: "manager", name: "Manager" },
  { id: "developer", name: "Developer" },
  { id: "tester", name: "Tester" },
  { id: "maintainer", name: "Maintainer" },
  { id: "productionTester", name: "Production tester" },
  { id: "productionMaintainer", name: "Production maintainer" },
];

// Slots an agent may not sit in.
const PEOPLE_ONLY_SLOTS = new Set<TeamSlot>([
  "manager",
  "productionTester",
  "productionMaintainer",
]);

type BulkResponse = {
  count: number;
  skipped: Array<{ id: string; title: string; reason: string; missingSlot?: string }>;
};

/** What the server says would block this move, counted per reason. */
type Preflight = {
  ready: number;
  total: number;
  blockers: Array<{ reason: string; count: number; ids: string[] }>;
  missingSlots: SlotGap[];
};

/** Tickets that only need someone in a team slot, by slot. */
type SlotGap = { slot: string; label: string; ids: string[] };

const SLOT_LABEL: Record<string, string> = {
  managerId: "Manager",
  developerId: "Developer",
  testerId: "Tester",
  maintainerId: "Maintainer",
  productionTesterId: "Production tester",
  productionMaintainerId: "Production maintainer",
};

export function BulkActions({
  selectedIds,
  workItems,
  users,
  releases = [],
  canSeeAll = false,
  canOverrideStatus = false,
  exportSort,
  onClear,
}: {
  selectedIds: string[];
  /** The current page's rows — filtered down to the ticked ids to
   *  compute the report bar's eligibility (billing mode, price,
   *  customer). Mirrors how the TE bulk bars read `entries`. */
  workItems: WorkItemRow[];
  users: Option[];
  /** Open releases of the current project; empty on the global list
   *  (releases are project-scoped there) — hides the Release action. */
  releases?: Option[];
  /** Admin / accountant — enables the "Create report" bar. */
  canSeeAll?: boolean;
  /** Managers and admins get the status override — the same bypass the
   *  per-ticket panel offers, applied to the selection. */
  canOverrideStatus?: boolean;
  /** `sort=…&dir=…` of the table, so the exported selection keeps its order. */
  exportSort?: string | undefined;
  onClear: () => void;
}) {
  const router = useRouter();
  const navigate = useNavigate();
  const [activeAction, setActiveAction] = useState<BulkAction | null>(null);
  const [value, setValue] = useState("");
  const [teamSlot, setTeamSlot] = useState<TeamSlot>("developer");
  const [loading, setLoading] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [moveActionId, setMoveActionId] = useState("");
  const [overrideState, setOverrideState] = useState("");
  const [overrideStates, setOverrideStates] = useState<string[]>([]);
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [overrideGaps, setOverrideGaps] = useState<SlotGap[]>([]);
  const [slotPicks, setSlotPicks] = useState<Record<string, string>>({});
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [moveAssigneeId, setMoveAssigneeId] = useState("");
  const [moveEstimatedHours, setMoveEstimatedHours] = useState("");
  const [moveComment, setMoveComment] = useState("");

  // Report bar — every ticked row must be a fixed-price WI with a price
  // set, not already on a report or manually flagged as invoiced
  // elsewhere, and share one customer (a report is per-customer). The
  // server re-checks the same contract.
  const selectedIdSet = new Set(selectedIds);
  const selectedRows = workItems.filter((w) => selectedIdSet.has(w.id));

  // "Move" only ever offers actions available on EVERY selected row —
  // the intersection of each row's server-resolved `availableActions`.
  // Nothing here that a row's own action buttons wouldn't already allow.
  const commonMoveActions: WorkflowMoveAction[] = (() => {
    const [first, ...rest] = selectedRows;
    if (!first) return [];
    return first.availableActions.filter((a) =>
      rest.every((r) => r.availableActions.some((ra) => ra.id === a.id)),
    );
  })();
  const selectedMoveAction = commonMoveActions.find((a) => a.id === moveActionId) ?? null;

  // The states this selection could be forced into — server-side, because
  // a row's own `availableActions` is already filtered by its current
  // state and an override exists to get past exactly that.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedIds is rebuilt every render
  useEffect(() => {
    if (activeAction !== "move" || !canOverrideStatus) return;
    let cancelled = false;
    api
      .get<{ states: string[] }>(
        `/api/work-items/bulk-actions/states?ids=${encodeURIComponent(selectedIds.join(","))}`,
      )
      .then((res) => !cancelled && setOverrideStates(res.states))
      .catch(() => !cancelled && setOverrideStates([]));
    return () => {
      cancelled = true;
    };
  }, [activeAction, canOverrideStatus, selectedIds.join(",")]);

  // The override is checked the same way, for the team slots its target state needs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedIds is rebuilt every render
  useEffect(() => {
    setSlotPicks({});
    if (activeAction !== "move" || !overrideState) {
      setOverrideGaps([]);
      return;
    }
    let cancelled = false;
    api
      .post<BulkResponse>("/api/work-items/bulk-actions/status", {
        ids: selectedIds,
        targetState: overrideState,
        dryRun: true,
      })
      .then((res) => {
        if (cancelled) return;
        const bySlot = new Map<string, string[]>();
        for (const row of res.skipped) {
          if (row.missingSlot)
            bySlot.set(row.missingSlot, [...(bySlot.get(row.missingSlot) ?? []), row.id]);
        }
        setOverrideGaps(
          [...bySlot.entries()].map(([slot, ids]) => ({
            slot,
            label: SLOT_LABEL[slot] ?? slot,
            ids,
          })),
        );
      })
      .catch(() => !cancelled && setOverrideGaps([]));
    return () => {
      cancelled = true;
    };
  }, [activeAction, overrideState, selectedIds.join(",")]);

  // Ask what would block the move as soon as an action is picked, so the
  // missing release and the missing developer show up together instead of
  // one per attempt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedIds is rebuilt every render
  useEffect(() => {
    setSlotPicks({});
    if (activeAction !== "move" || !moveActionId) {
      setPreflight(null);
      return;
    }
    let cancelled = false;
    setPreflightBusy(true);
    api
      .post<Preflight>("/api/work-items/bulk-actions/preflight", {
        ids: selectedIds,
        actionId: moveActionId,
      })
      .then((res) => !cancelled && setPreflight(res))
      .catch(() => !cancelled && setPreflight(null))
      .finally(() => !cancelled && setPreflightBusy(false));
    return () => {
      cancelled = true;
    };
  }, [activeAction, moveActionId, selectedIds.join(",")]);
  const moveNeedsAssignee = selectedMoveAction?.requiredFields.includes("assigneeId") ?? false;
  const moveNeedsHours = selectedMoveAction?.requiredFields.includes("estimatedHours") ?? false;
  const moveCommentRequired = selectedMoveAction?.requiredFields.includes("comment") ?? false;
  const moveNeedsComment =
    moveCommentRequired || (selectedMoveAction?.optionalFields?.includes("comment") ?? false);
  const reportCustomerIds = new Set(selectedRows.map((w) => w.project.customer?.id ?? null));
  const reportEligible =
    canSeeAll &&
    selectedRows.length > 0 &&
    selectedRows.every(
      (w) =>
        w.billingMode === "fixed" &&
        w.fixedPrice !== null &&
        w._count.invoiceItems === 0 &&
        w.manuallyInvoicedAt === null,
    ) &&
    reportCustomerIds.size === 1 &&
    !reportCustomerIds.has(null);
  const reportIds = reportEligible ? selectedRows.map((w) => w.id) : [];
  const reportCustomerId = reportEligible ? ([...reportCustomerIds][0] as string) : null;
  const reportCustomerName = reportEligible
    ? (selectedRows[0]?.project.customer?.name ?? null)
    : null;

  async function createReport() {
    if (reportBusy || reportIds.length === 0) return;
    setReportBusy(true);
    try {
      const { batchId } = await api.post<{ batchId: string }>(
        "/api/billing/report-from-work-items",
        { workItemIds: reportIds },
      );
      toast({ title: "Report created", variant: "success" });
      onClear();
      navigate({ to: "/billing/$id", params: { id: batchId } });
    } catch (err) {
      toast({
        title: "Couldn't create report",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setReportBusy(false);
    }
  }

  /** Shared toast + refresh for both bulk endpoints — a partial result
   *  surfaces the per-row skip reasons instead of pretending everything
   *  applied. */
  function reportBulkResult(res: BulkResponse, successTitle: string) {
    if (res.skipped.length > 0) {
      // Grouped by reason: six tickets refused for the same missing field
      // is one sentence, not six lines of raw log-line titles.
      const byReason = new Map<string, number>();
      for (const s of res.skipped) byReason.set(s.reason, (byReason.get(s.reason) ?? 0) + 1);
      toast({
        title: `Applied to ${res.count} of ${selectedIds.length}`,
        description: [...byReason.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([reason, n]) => (n === 1 ? reason : `${n}× ${reason}`))
          .join("\n"),
        variant: res.count > 0 ? "default" : "destructive",
      });
    } else {
      toast({ title: successTitle, variant: "success" });
    }
  }

  async function executeAction() {
    if (!activeAction || activeAction === "move") return;
    setLoading(true);
    try {
      const action = activeAction === "team" ? teamSlot : activeAction;
      const res = await api.patch<BulkResponse>("/api/work-items/bulk", {
        ids: selectedIds,
        action,
        value: activeAction !== "delete" ? value : undefined,
      });
      reportBulkResult(
        res,
        activeAction === "delete"
          ? `Deleted ${res.count} work item${res.count === 1 ? "" : "s"}`
          : `Updated ${res.count} work item${res.count === 1 ? "" : "s"}`,
      );
      onClear();
      await router.invalidate();
    } catch (err) {
      toast({
        title: "Bulk action failed",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setActiveAction(null);
      setValue("");
    }
  }

  /** Manager status override on the selection: a target state, resolved to
   *  each row's own action by the server. Skips the workflow's path the
   *  same way the per-ticket override does, and is refused row by row for
   *  anyone without the manager bypass. */
  async function executeStatusOverride() {
    if (!overrideState) return;
    setLoading(true);
    try {
      const res = await api.post<BulkResponse>("/api/work-items/bulk-actions/status", {
        ids: selectedIds,
        targetState: overrideState,
        ...(Object.keys(slotPicks).length ? { team: slotPicks } : {}),
      });
      reportBulkResult(res, `Status set on ${res.count} work item${res.count === 1 ? "" : "s"}`);
      onClear();
      await router.invalidate();
    } catch (err) {
      toast({
        title: "Status override failed",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setActiveAction(null);
      setOverrideState("");
    }
  }

  async function executeMove() {
    if (!selectedMoveAction) return;
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {};
      if (moveNeedsAssignee) payload.assigneeId = moveAssigneeId;
      if (moveNeedsHours) payload.estimatedHours = Number.parseFloat(moveEstimatedHours);
      if (moveComment.trim()) payload.comment = moveComment.trim();
      if (Object.keys(slotPicks).length) payload.team = slotPicks;
      const res = await api.post<BulkResponse>("/api/work-items/bulk-actions", {
        ids: selectedIds,
        actionId: selectedMoveAction.id,
        ...(Object.keys(payload).length > 0 ? { payload } : {}),
      });
      reportBulkResult(
        res,
        `${selectedMoveAction.label} applied to ${res.count} work item${res.count === 1 ? "" : "s"}`,
      );
      onClear();
      await router.invalidate();
    } catch (err) {
      toast({
        title: "Bulk move failed",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setActiveAction(null);
      setMoveActionId("");
      setMoveAssigneeId("");
      setMoveEstimatedHours("");
      setMoveComment("");
    }
  }

  function openAction(action: BulkAction) {
    setValue("");
    setMoveActionId("");
    setMoveAssigneeId("");
    setMoveEstimatedHours("");
    setMoveComment("");
    setActiveAction(action);
  }

  return (
    <>
      <BulkActionBar selectedCount={selectedIds.length} onClear={onClear} itemNoun="work item">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5"
          onClick={() => openAction("assignee")}
        >
          <User className="h-3.5 w-3.5" />
          Assignee
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5"
          onClick={() => openAction("priority")}
        >
          <AlertCircle className="h-3.5 w-3.5" />
          Priority
        </Button>
        {releases.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5"
            onClick={() => openAction("release")}
          >
            <Tag className="h-3.5 w-3.5" />
            Release
          </Button>
        )}
        {commonMoveActions.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5"
            onClick={() => openAction("move")}
          >
            <Workflow className="h-3.5 w-3.5" />
            Move
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5"
          onClick={() => openAction("team")}
        >
          <Users className="h-3.5 w-3.5" />
          Team
        </Button>
        {/* The exports take the selection itself, so they work the same on the
            global list as on a project's — plain downloads, no dialog. */}
        <Button asChild size="sm" variant="outline" className="h-8 text-xs gap-1.5">
          <a
            href={`/api/work-items/export?ids=${selectedIds.join(",")}${exportSort ?? ""}`}
            download
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            CSV
          </a>
        </Button>
        <Button asChild size="sm" variant="outline" className="h-8 text-xs gap-1.5">
          <a
            href={`/api/work-items/export/pdf?ids=${selectedIds.join(",")}${exportSort ?? ""}`}
            download
          >
            <FileText className="h-3.5 w-3.5" />
            PDF
          </a>
        </Button>
        <Button
          size="sm"
          variant="destructive"
          className="h-8 text-xs gap-1.5"
          onClick={() => openAction("delete")}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </Button>
      </BulkActionBar>

      {reportEligible && reportIds.length > 0 && reportCustomerId && (
        <BulkActionBar
          selectedCount={reportIds.length}
          totalCount={selectedIds.length}
          onClear={onClear}
          stackIndex={1}
          itemNoun="work item"
        >
          <AddToDraftReportDialog
            kind="work-items"
            ids={reportIds}
            customerId={reportCustomerId}
            customerName={reportCustomerName}
            onDone={async () => {
              onClear();
              await router.invalidate();
            }}
          >
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={reportBusy}>
              Send to existing report
            </Button>
          </AddToDraftReportDialog>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={reportBusy}
            onClick={() => void createReport()}
          >
            Create report
          </Button>
        </BulkActionBar>
      )}

      <Dialog open={activeAction !== null} onOpenChange={(o) => !o && setActiveAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {activeAction === "assignee" && "Change Assignee"}
              {activeAction === "priority" && "Change Priority"}
              {activeAction === "release" && "Assign to Release"}
              {activeAction === "team" && "Set Team Member"}
              {activeAction === "delete" && "Delete Work Items"}
              {activeAction === "move" && "Move Work Items"}
            </DialogTitle>
          </DialogHeader>

          {activeAction === "delete" ? (
            <p className="text-sm text-muted-foreground">
              Are you sure you want to permanently delete {selectedIds.length} work item
              {selectedIds.length !== 1 ? "s" : ""}? The GitLab issues are deleted too. This cannot
              be undone.
            </p>
          ) : activeAction === "move" ? (
            <div className="space-y-3 py-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a custom Select */}
              <label className="block">
                <span className="text-sm font-medium mb-1.5 block">Action</span>
                <Select
                  value={moveActionId}
                  onChange={setMoveActionId}
                  options={[
                    { value: "", label: "— select —" },
                    ...commonMoveActions.map((a) => ({ value: a.id, label: a.label })),
                  ]}
                />
              </label>
              {selectedMoveAction?.description && (
                <p className="text-xs text-muted-foreground">{selectedMoveAction.description}</p>
              )}
              {canOverrideStatus && overrideStates.length > 0 && (
                <div className="border-t pt-3 space-y-1.5">
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a custom Select */}
                  <label className="block">
                    <span className="text-sm font-medium mb-1.5 block">
                      Status override (manager)
                    </span>
                    <Select
                      value={overrideState}
                      onChange={(v) => {
                        setOverrideState(v);
                        if (v) setMoveActionId("");
                      }}
                      options={[
                        { value: "", label: "— none —" },
                        ...overrideStates.map((st) => ({
                          value: st,
                          label: displayStateName(st),
                        })),
                      ]}
                    />
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Sets the state directly, skipping the path the workflow would take. A row whose
                    workflow can't reach it is named in the result.
                  </p>
                </div>
              )}
              {preflightBusy && (
                <p className="text-xs text-muted-foreground">Checking what would block this…</p>
              )}
              {preflight && preflight.blockers.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 space-y-1">
                  <p className="text-xs font-medium">
                    {preflight.ready} of {preflight.total} can move. Fix these first:
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {preflight.blockers.map((b) => (
                      <li key={b.reason}>
                        {b.count === preflight.total ? `All ${b.count}` : b.count}
                        {b.count === 1 ? " ticket" : " tickets"}: {b.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(overrideState ? overrideGaps : (preflight?.missingSlots ?? [])).map((gap) => (
                <SlotPicker
                  key={gap.slot}
                  workItemId={gap.ids[0] as string}
                  missing={{
                    slot: gap.slot,
                    label: `${gap.label} (${gap.ids.length} ticket${gap.ids.length === 1 ? "" : "s"})`,
                  }}
                  value={slotPicks[gap.slot] ?? ""}
                  onChange={(v) => setSlotPicks((p) => ({ ...p, [gap.slot]: v }))}
                />
              ))}
              {moveNeedsAssignee && (
                // biome-ignore lint/a11y/noLabelWithoutControl: wraps a custom Combobox
                <label className="block">
                  <span className="text-sm font-medium mb-1.5 block">Assignee</span>
                  <Combobox
                    value={moveAssigneeId}
                    onChange={setMoveAssigneeId}
                    placeholder="— select —"
                    emptyLabel="No users"
                    options={users.map((o) => ({ value: o.id, label: o.name }))}
                  />
                </label>
              )}
              {moveNeedsHours && (
                <label className="block">
                  <span className="text-sm font-medium mb-1.5 block">Estimated hours</span>
                  <input
                    type="number"
                    step="0.25"
                    min="0.25"
                    value={moveEstimatedHours}
                    onChange={(e) => setMoveEstimatedHours(e.target.value)}
                    placeholder="0.00"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </label>
              )}
              {moveNeedsComment && (
                <label className="block">
                  <span className="text-sm font-medium mb-1.5 block">
                    Comment{" "}
                    {!moveCommentRequired && (
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    )}
                  </span>
                  <textarea
                    value={moveComment}
                    onChange={(e) => setMoveComment(e.target.value)}
                    placeholder="Add a note…"
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </label>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {activeAction === "team" && (
                // biome-ignore lint/a11y/noLabelWithoutControl: wraps a custom Select
                <label className="block">
                  <span className="text-sm font-medium mb-1.5 block">Team slot</span>
                  <Select
                    value={teamSlot}
                    onChange={(v) => setTeamSlot(v as TeamSlot)}
                    options={TEAM_SLOTS.map((s) => ({ value: s.id, label: s.name }))}
                  />
                </label>
              )}
              {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps either a custom Select or a custom Combobox */}
              <label className="block">
                <span className="text-sm font-medium mb-1.5 block">
                  {activeAction === "assignee" && "Assign to"}
                  {activeAction === "priority" && "New priority"}
                  {activeAction === "release" && "Release"}
                  {activeAction === "team" && "User"}
                </span>
                {activeAction === "priority" ? (
                  <Select
                    value={value}
                    onChange={setValue}
                    options={[
                      { value: "", label: "— select —" },
                      ...PRIORITIES.map((o) => ({ value: o.id, label: o.name })),
                    ]}
                  />
                ) : (
                  <Combobox
                    value={value}
                    onChange={setValue}
                    placeholder={activeAction === "release" ? "— select release —" : "— select —"}
                    emptyLabel={activeAction === "release" ? "No open releases" : "No users"}
                    options={(activeAction === "release"
                      ? releases
                      : activeAction === "team" && PEOPLE_ONLY_SLOTS.has(teamSlot)
                        ? users.filter((u) => !u.id.startsWith("user-agent-"))
                        : users
                    ).map((o) => ({
                      value: o.id,
                      label: o.name,
                    }))}
                  />
                )}
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setActiveAction(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={activeAction === "delete" ? "destructive" : "default"}
              disabled={
                loading ||
                (activeAction === "move"
                  ? overrideState
                    ? false // a state override needs nothing but the state
                    : !selectedMoveAction ||
                      (moveNeedsAssignee && !moveAssigneeId) ||
                      (moveNeedsHours && !(Number.parseFloat(moveEstimatedHours) > 0)) ||
                      (moveCommentRequired && !moveComment.trim())
                  : activeAction !== "delete" && !value)
              }
              onClick={
                activeAction === "move"
                  ? overrideState
                    ? executeStatusOverride
                    : executeMove
                  : executeAction
              }
            >
              {loading ? (
                <LoadingLabel text="Working" />
              ) : activeAction === "delete" ? (
                "Delete"
              ) : (
                "Apply"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
